import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "Store/useAuthStore";
import useJobDetailsV2 from "Hooks/useJobDetailsV2";
import useFullscreenToggle from "Hooks/useFullscreenToggle";
import analytics from "Utils/analytics";
import { debounce } from "Utils/debounce";
import {
  PAIC_VIEW_MODES,
  PAIC_TAB_KEYS,
  PAIC_DOCUMENT_TYPES,
} from "constants/peoplePlusAI.constants";
import { PAIC_ROUTES } from "constants/paicRoutes.constants";
import SbDownloadMarkdownPdfV2 from "Components/HumanEditorV2/SbDownloadMarkdownPdfV2";
import {
  SbPACDocumentHeaderV2,
  SbPACDocumentToolbarV2,
  SbPACDocumentContentV2,
  SbPACDocumentEditPanelV2,
} from "Components/PACDocumentViewV2";
import AgentBuilderPanel from "Components/AgentBuilder/AgentBuilderPanel";
import { startAgentBuild, getAgentBuildStatus, getAgentBuildDownloadUrl } from "Api/apiService";
import useAgentBuilderStore from "Store/useAgentBuilderStore";



import { SbNotification } from "uiCore";
import usePAICStore from "Store/usePAICStore";
import { PAIC_PROJECT_SOURCE, PAIC_VIEW_ONLY_MESSAGE } from "constants/peoplePlusAI.constants";
import usePACJdPrdViewV2 from "./usePACJdPrdViewV2";
import {
  PageContainer,
  ContentSection,
  DocumentCard,
  EditorRegion,
  HiddenDownloadMount,
} from "../shared/DocumentView.styles";

const agentBuildCacheKey = (agentId) => `sb_agent_build_id_${agentId}`;

// PRD §5.14 — tooltip content strings for Agent JD/PRD View
const TOOLTIP_CLOSE =
  "Close the document view and return to the Group Agents screen.";
const TOOLTIP_DOCUMENT_TYPE = "Switch between Agent JD and Agent PRD views.";
const TOOLTIP_SELECT_AGENT =
  "Switch to view documents for a different agent in the current version.";
const TOOLTIP_PREV_AGENT = "View documents for the previous agent in the list.";
const TOOLTIP_NEXT_AGENT = "View documents for the next agent in the list.";
const TOOLTIP_EDIT =
  "Open the editor to make changes to this agent's JD or PRD.";
const TOOLTIP_DOWNLOAD = "Download this document as a PDF file.";

const SbPACJdPrdViewV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hiddenDownloadRef = useRef(null);
  const buildNotifUrlRef = useRef(null);
  const email = useAuthStore((s) => s.email);

  const groupName = usePAICStore((s) => s.groupName);
  const projectSource = usePAICStore((s) => s.projectSource);
  // Only the group's Owner or a Delegated user may edit; Shared (and any other/unset
  // access) is view-only.
  const isReadOnly =
    projectSource !== PAIC_PROJECT_SOURCE.OWNED &&
    projectSource !== PAIC_PROJECT_SOURCE.DELEGATED;
  const [downloadRequested, setDownloadRequested] = useState(false);
  const { isFullscreen, handleFullscreenToggle } = useFullscreenToggle();

  // Agent Builder state
  const [buildId, setBuildId]               = useState(null);
  const [buildStatus, setBuildStatus]       = useState(null);  // null | QUEUED | BUILDING | DONE | FAILED | TIMED_OUT
  const [buildStatusLoading, setBuildStatusLoading] = useState(false);
  const [showBuilderPanel, setShowBuilderPanel] = useState(false);
  const [builderError, setBuilderError]     = useState(null);
  const [buildTokenUsage, setBuildTokenUsage] = useState(null);

  const {
    buildId:          abStoreBuildId,
    isMinimized:      abStoreMinimized,
    pendingExpand:    abStorePendingExpand,
    startBuild:       abStartBuild,
    setStatus:        abSetStatus,
    setMinimized:     abSetMinimized,
    setPendingExpand: abSetPendingExpand,
    setTokenUsage:    abSetTokenUsage,
    clearBuild:       abClearBuild,
  } = useAgentBuilderStore();



  const rawDefaultType = new URLSearchParams(location.search).get("defaultType");

  const defaultType =
    rawDefaultType === "prd"
      ? "agent-prd"
      : rawDefaultType === "jd"
        ? "agent-jd"
        : rawDefaultType;

  const { versionData = [], selectedJdVersion, setSelectedJdVersion } = useJobDetailsV2();

  const selectedVersionData = useMemo(
    () =>
      versionData.find(
        (version) => String(version.version) === String(selectedJdVersion),
      ),
    [versionData, selectedJdVersion],
  );

  const {
    documentType,
    viewMode,
    selectedDocumentAgentId,
    jdPrdLoading,
    aiAgentOptions,
    markdownData,
    versionLabel,
    modifiedText,
    ownerText,
    documentStatus,
    selectedDocumentData,
    selectedVersionWithAgents,
    setDocumentType,
    setSelectedDocumentAgentId,
    handlePagination,
    enterEditMode,
    cancelEditMode,
    prdVersionOptions,
    prdVersionLabel,
    selectedPrdVersion,
    setSelectedPrdVersion,
    savingPrd,
    handleSavePrd,
  } = usePACJdPrdViewV2({
    defaultType,
  });

  const isPrd = documentType === PAIC_DOCUMENT_TYPES.AGENT_PRD;
  const [editedPrd, setEditedPrd] = useState(null);
  const originalPrdRef = useRef(null);
  const editReadyRef = useRef(false);

  // MDXEditor fires onChange while reformatting on mount; wait for those to settle
  // (debounced) before treating onChange as a real edit.
  const markEditReady = useMemo(
    () =>
      debounce(() => {
        editReadyRef.current = true;
      }, 200),
    [],
  );

  const resetPrdEdit = () => {
    markEditReady.cancel();
    originalPrdRef.current = null;
    editReadyRef.current = false;
    setEditedPrd(null);
  };

  useEffect(() => {
    if (!isPrd || viewMode !== PAIC_VIEW_MODES.EDIT) return undefined;
    originalPrdRef.current = null;
    editReadyRef.current = false;
    setEditedPrd(null);
    // Start the settle timer; the init emissions below keep resetting it until they stop.
    markEditReady();
    return () => markEditReady.cancel();
  }, [isPrd, viewMode, selectedDocumentAgentId, selectedPrdVersion, markEditReady]);

  const onPrdEdit = (md) => {
    // Until the init burst settles, keep the latest emission as the baseline (not a user
    // edit) and restart the settle timer. Once settled, onChange is a genuine edit.
    if (!editReadyRef.current) {
      originalPrdRef.current = md;
      markEditReady();
      return;
    }
    setEditedPrd(md);
  };

  const isPrdChanged =
    isPrd &&
    editedPrd != null &&
    editedPrd.trim() !== (originalPrdRef.current ?? "").trim();

  const pageTitle = `Job Details: ${selectedVersionData?.job_title_for_jd ||
    selectedVersionData?.job_title ||
    "People + AI Agent PRD"
    }`;

  const handleCloseButton = () => {
    analytics.trackPAICCloseDocuments({
      from: "pac_jd_prd_view",
      email,
      description: "Closed JD/PRD view",
    });

    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(PAIC_ROUTES.GROUP_AGENTS);
    }
  };

  // This keeps the old behavior:
  // toolbar click -> track analytics -> hidden PDF link gets clicked.
  // We just replaced setTimeout with requestAnimationFrame polling.
  const triggerPdfDownload = () => {
    analytics.trackDownloadJD({
      from: "pac_jd_prd_view",
      email,
      description: `Downloaded ${documentType}`,
    });

    setDownloadRequested(true);
  };

  useEffect(() => {
    if (!downloadRequested) return undefined;

    let frameId;

    const tryClickDownloadLink = () => {
      // PDFDownloadLink renders an anchor tag internally.
      const link = hiddenDownloadRef.current?.querySelector("a");

      if (link && typeof link.click === "function" && link.href && link.href.startsWith("blob:")) {
        link.click();
        setDownloadRequested(false);
        return;
      }

      // Retry on the next frame until the link is mounted and ready.
      frameId = window.requestAnimationFrame(tryClickDownloadLink);
    };

    tryClickDownloadLink();

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [downloadRequested]);

  // const handleCompareSummary = () => {
  //   console.log("Comparison Summary clicked");
  //   // TODO: Implement comparison summary logic
  // };

  // const handleDiffCheck = () => {
  //   console.log("Difference Checker clicked");
  //   // TODO: Implement difference checker logic
  // };

  // const handlePublishToggle = () => {
  //   console.log("Publish / Unpublish clicked");
  //   // TODO: add publish unpublish API call here
  // };

  // When the global ghost pill expand is clicked while already on this page, re-open the panel
  useEffect(() => {
    if (!abStoreMinimized && abStoreBuildId && buildId && abStoreBuildId === buildId && !showBuilderPanel) {
      setShowBuilderPanel(true);
    }
  }, [abStoreMinimized]);

  // Reset build state on agent switch, then restore from cache if a prior build exists
  useEffect(() => {
    setBuildId(null);
    setBuildStatus(null);
    setBuildStatusLoading(false);
    setShowBuilderPanel(false);
    setBuilderError(null);
    setBuildTokenUsage(null);

    if (!selectedDocumentAgentId) return;

    const cachedBuildId = localStorage.getItem(agentBuildCacheKey(selectedDocumentAgentId));
    if (!cachedBuildId) {
      // Fall back to build data passed via route state (populated from the group agents table).
      // Covers the case where the agent was built in a different browser session and localStorage
      // has no entry — the main agent table already shows Download, but the PRD view showed
      // "Build Agent" because it only checked localStorage.
      const routeBuild = location.state?.agentBuilds?.[selectedDocumentAgentId];
      if (routeBuild?.build_id && routeBuild?.build_status === "DONE") {
        setBuildId(routeBuild.build_id);
        setBuildStatus("DONE");
        localStorage.setItem(agentBuildCacheKey(selectedDocumentAgentId), routeBuild.build_id);
      }
      return;
    }

    setBuildStatusLoading(true);
    getAgentBuildStatus(cachedBuildId)
      .then((res) => {
        if (res?.status) {
          setBuildId(cachedBuildId);
          setBuildStatus(res.status);
          if (res.token_usage) setBuildTokenUsage(res.token_usage);
          // Re-open the panel if:
          //   (a) user minimized and is still on this page [abStoreMinimized], OR
          //   (b) user clicked expand from a different page [abStorePendingExpand]
          const storeMatchesThis = abStoreBuildId === cachedBuildId &&
            (abStoreMinimized || abStorePendingExpand);
          if (storeMatchesThis) {
            setShowBuilderPanel(true);
            abSetMinimized(false);
            if (abStorePendingExpand) abSetPendingExpand(false);
          }
        }
      })
      .catch(() => {
        localStorage.removeItem(agentBuildCacheKey(selectedDocumentAgentId));
      })
      .finally(() => {
        setBuildStatusLoading(false);
      });
  }, [selectedDocumentAgentId]);

  const handleBuildAgent = async (force = false) => {
    if (!selectedDocumentAgentId) return;

    // Capture current page URL (dev params stripped) — sent to backend so the bell notification
    // can deep-link back to this exact PRD page, even if the user navigates away before it completes
    const notifParams = new URLSearchParams(window.location.search);
    ["_buildStatus", "_mockBuild", "_mockPanel"].forEach((k) => notifParams.delete(k));
    const notifSearch = notifParams.toString() ? `?${notifParams.toString()}` : "";
    buildNotifUrlRef.current = window.location.pathname + notifSearch;

    // If a build is already tracked and the panel was just closed, reopen it
    if (buildId && !force && (buildStatus === "QUEUED" || buildStatus === "BUILDING" || buildStatus === "DONE")) {
      setShowBuilderPanel(true);
      abSetMinimized(false);
      return;
    }

    try {
      setBuildStatus("QUEUED");
      const res = await startAgentBuild(selectedDocumentAgentId, force, buildNotifUrlRef.current || "");
      const newBuildId = res.build_id;
      setBuildId(newBuildId);
      localStorage.setItem(agentBuildCacheKey(selectedDocumentAgentId), newBuildId);
      if (res.already_built) {
        setBuildStatus("DONE");
        setShowBuilderPanel(true);
        setBuilderError(null);
        abSetStatus("DONE");
        return;
      }
      setBuildStatus("BUILDING");
      setShowBuilderPanel(true);
      setBuilderError(null);
      abStartBuild({ buildId: newBuildId, agentId: selectedDocumentAgentId, returnPath: buildNotifUrlRef.current });
    } catch (err) {
      // 409 = a build is already in progress — recover by attaching to that build
      if (err?.response?.status === 409 && err.response.data?.build_id) {
        const existingBuildId = err.response.data.build_id;
        setBuildId(existingBuildId);
        setBuildStatus("BUILDING");
        setShowBuilderPanel(true);
        setBuilderError(null);
        localStorage.setItem(agentBuildCacheKey(selectedDocumentAgentId), existingBuildId);
        abStartBuild({ buildId: existingBuildId, agentId: selectedDocumentAgentId, returnPath: buildNotifUrlRef.current });
        return;
      }
      setBuildStatus("FAILED");
      setBuilderError(err?.response?.data?.error || "Failed to start build");
    }
  };

  const handleBuildComplete = (gcsPath, tokenUsageData) => {
    setBuildStatus("DONE");
    abSetStatus("DONE");
    if (tokenUsageData) {
      setBuildTokenUsage(tokenUsageData);
      abSetTokenUsage(tokenUsageData);
    }
  };

  const handleBuildError = (status, message) => {
    setBuildStatus(status);
    abSetStatus(status);
    setBuilderError(message);
  };

  const handleDownloadAgent = async () => {
    if (!buildId) return;
    try {
      const res = await getAgentBuildDownloadUrl(buildId);
      const a = document.createElement("a");
      a.href = res.download_url;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const getPopupContainer = (node) => node?.parentElement || document.body;

  const TAB_ITEMS = [
    { key: PAIC_TAB_KEYS.JOBS_LIBRARY, label: "Jobs Library", disabled: true },
    { key: PAIC_TAB_KEYS.AGENTS_LIBRARY, label: "Agents Library", disabled: true },
    { key: PAIC_TAB_KEYS.PEOPLE_AI, label: "People + AI" },
    { key: PAIC_TAB_KEYS.SKILLS_BRIDGER, label: "Skills Bridger", disabled: true },
    { key: PAIC_TAB_KEYS.LEARNING_CATALYST, label: "Learning Catalyst", disabled: true },
    { key: PAIC_TAB_KEYS.TALENT_MOBILITY, label: "Talent Mobility", disabled: true },
  ];

  const DOCUMENT_TYPE_OPTIONS = [
    { value: PAIC_DOCUMENT_TYPES.AGENT_PRD, label: "Agent PRD" },
    { value: PAIC_DOCUMENT_TYPES.AGENT_JD, label: "Agent JD" },
  ];

  const formatVersion = (value) => {
    const num = Number(value);
    return Number.isNaN(num) ? "1.0" : num.toFixed(1);
  };

  const versionOptions =
    versionData && versionData.length > 0
      ? versionData.map((item) => ({
        label: `JD Version - ${formatVersion(item.version)}`,
        value: formatVersion(item.version),
      }))
      : [{ label: "JD Version - 1.0", value: "1.0" }];

  const toolbarVersionLabel = selectedVersionData?.version
    ? `JD Version - ${selectedVersionData.version}`
    : "JD Version - 1";

  // Build a clean PDF file name: GroupName_AgentName_PRD.pdf or GroupName_AgentName_JD.pdf
  const pdfFileName = useMemo(() => {
    const sanitize = (str) => (str || "").replace(/[^a-zA-Z0-9_\- ]/g, "").trim().replace(/\s+/g, "_");
    const cleanGroupName = sanitize(groupName) || "Group";
    const agentName = sanitize(selectedDocumentData?.ai_agent_name) || "Agent";
    const docLabel = documentType === "agent-prd" ? "PRD" : "JD";
    return `${cleanGroupName}_${agentName}_${docLabel}.pdf`;
  }, [groupName, selectedDocumentData, documentType]);

  return (
    <PageContainer $isFullscreen={isFullscreen}>
      {!isFullscreen && (
        <SbPACDocumentHeaderV2
          title={pageTitle}
          viewMode={viewMode}
          activeTab={PAIC_TAB_KEYS.PEOPLE_AI}
          tabItems={TAB_ITEMS}
          showDocumentTypeSelector={true}
          documentType={documentType}
          documentTypeOptions={DOCUMENT_TYPE_OPTIONS}
          onDocumentTypeChange={setDocumentType}
          documentTypeSelectorWidth="170px"
          documentTypeTooltip={TOOLTIP_DOCUMENT_TYPE}
          showNavigation={true}
          selectedItemId={selectedDocumentAgentId}
          itemOptions={aiAgentOptions}
          onItemChange={setSelectedDocumentAgentId}
          onPrev={() => handlePagination("PREV")}
          onNext={() => handlePagination("NEXT")}
          navigationPlaceholder="Select Agent"
          navigationWidth="280px"
          selectItemTooltip={TOOLTIP_SELECT_AGENT}
          prevTooltip={TOOLTIP_PREV_AGENT}
          nextTooltip={TOOLTIP_NEXT_AGENT}
          onClose={handleCloseButton}
          closeTooltip={TOOLTIP_CLOSE}
          getPopupContainer={getPopupContainer}
        />
      )}

      <ContentSection>
        <DocumentCard>
          <SbPACDocumentToolbarV2
            viewMode={viewMode}
            showVersionMeta={true}
            modifiedText={modifiedText}
            ownerText={ownerText}
            documentStatus={documentStatus}
            showCompareAction={false}   /* was true — commented out for now */
            showDiffAction={false}      /* was true — commented out for now */
            showEditAction={true}
            showReviseAction={false}
            showDownloadAction={true}
            showFullscreenAction={true}
            isFullscreen={isFullscreen}
            /* onCompareSummary={handleCompareSummary} — commented out for now */
            /* onDiffCheck={handleDiffCheck} — commented out for now */
            editDisabled={isReadOnly}
            onEdit={isReadOnly ? () => SbNotification.info(PAIC_VIEW_ONLY_MESSAGE.title, PAIC_VIEW_ONLY_MESSAGE.description) : enterEditMode}
            onDownloadPdf={triggerPdfDownload}
            editTooltip={isReadOnly ? PAIC_VIEW_ONLY_MESSAGE.description : TOOLTIP_EDIT}
            downloadTooltip={TOOLTIP_DOWNLOAD}
            showVersionDropdown
            versionOptions={isPrd ? prdVersionOptions : versionOptions}
            versionLabel={isPrd ? prdVersionLabel : toolbarVersionLabel}
            selectedVersion={isPrd ? (selectedPrdVersion || undefined) : (selectedJdVersion ? formatVersion(selectedJdVersion) : "1.0")}
            onVersionChange={isPrd ? setSelectedPrdVersion : setSelectedJdVersion}
            showPublishAction={false}   /* was true — commented out for now */
          /* onPublishToggle={handlePublishToggle} — commented out for now */
            showBuildAgentAction={documentType === "agent-prd"}
            buildStatus={buildStatus}
            buildStatusLoading={buildStatusLoading}
            buildId={buildId}
            onBuildAgent={handleBuildAgent}
            onDownloadAgent={handleDownloadAgent}
            buildTokenUsage={buildStatus === "DONE" ? buildTokenUsage : null}
          />

          <EditorRegion>
            {viewMode === PAIC_VIEW_MODES.EDIT ? (
              <SbPACDocumentEditPanelV2
                content={markdownData}
                contentKey={`${documentType}-${selectedDocumentAgentId}-${isPrd ? selectedPrdVersion : ""}-edit`}
                onChange={onPrdEdit}
                onCancel={() => {
                  resetPrdEdit();
                  cancelEditMode();
                }}
                onSubmit={() => {
                  if (!isPrdChanged) return;
                  handleSavePrd(editedPrd);
                  resetPrdEdit();
                }}
                submitText="Save"
                submitting={savingPrd}
                submitDisabled={!isPrdChanged}
              />
            ) : (
              <SbPACDocumentContentV2
                loading={
                  jdPrdLoading ||
                  (isPrd && !!selectedDocumentAgentId && !selectedPrdVersion)
                }
                content={markdownData}
                contentKey={`${documentType}-${selectedDocumentAgentId}-${isPrd ? selectedPrdVersion : ""}`}
                emptyText="No agent documents available"
              />
            )}
          </EditorRegion>
        </DocumentCard>
      </ContentSection>

      <HiddenDownloadMount ref={hiddenDownloadRef}>
        {markdownData && (
          <SbDownloadMarkdownPdfV2
            key={`pdf-${documentType}-${selectedDocumentAgentId}-${isPrd ? selectedPrdVersion : selectedJdVersion}`}
            markdownContent={markdownData}
            jobData={selectedVersionData}
            pdfFileName={pdfFileName}
          />
        )}
      </HiddenDownloadMount>

      {showBuilderPanel && (
        <AgentBuilderPanel
          buildId={buildId}
          buildStatus={buildStatus}
          onComplete={handleBuildComplete}
          onError={handleBuildError}
          onClose={() => { setShowBuilderPanel(false); abClearBuild(); }}
          onMinimize={() => { setShowBuilderPanel(false); abSetMinimized(true); }}
          onRebuild={
            (buildStatus === "FAILED" || buildStatus === "TIMED_OUT")
              ? () => handleBuildAgent(true)
              : undefined
          }
        />
      )}
    </PageContainer>
  );
};

export default SbPACJdPrdViewV2;